import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

function ProtectedRoute({ children, requiredRole }) {
  const location = useLocation();
  
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    // If user data passed from AuthCallback, use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }
    
    const checkAuth = async () => {
      // Get backdoor token - check multiple sources
      let token = null;
      
      // 1. Check sessionStorage first (set by index.html before React loads)
      token = sessionStorage.getItem('backdoor_token');
      if (token) {
        console.log('ProtectedRoute: Found token in sessionStorage');
      }
      
      // 2. Check URL params as backup
      if (!token) {
        const urlParams = new URLSearchParams(window.location.search);
        token = urlParams.get('backdoor_token');
        if (token) {
          console.log('ProtectedRoute: Found token in URL params');
          sessionStorage.setItem('backdoor_token', token);
        }
      }
      
      // 3. Check location.search from React Router
      if (!token && location.search) {
        const searchParams = new URLSearchParams(location.search);
        token = searchParams.get('backdoor_token');
        if (token) {
          console.log('ProtectedRoute: Found token in location.search');
          sessionStorage.setItem('backdoor_token', token);
        }
      }
      
      try {
        console.log('ProtectedRoute: Checking auth, token present:', !!token, 'URL:', window.location.href);

        // Staff localStorage token is ONLY for staff routes. Using it on
        // superadmin/admin/partner routes overrides the login cookie and
        // bounces users back to the login page after a successful login.
        if (!token && requiredRole === 'staff') {
          const staffToken = localStorage.getItem('staff_session_token');
          if (staffToken) {
            token = staffToken;
          }
        }

        // Backdoor (and staff) identity lives in Bearer, while the shared cookie
        // may still be the superadmin session. Set axios defaults BEFORE children
        // mount so pages like StaffPage don't race the CAD interceptor and call
        // /admin/* with the wrong cookie-only auth (403 → empty lists after refresh).
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else if (requiredRole !== 'staff') {
          // Cookie-based admin/superadmin/partner — clear leftover staff Bearer
          delete axios.defaults.headers.common['Authorization'];
        }

        // Use token header if available, otherwise fall back to cookies
        const config = token
          ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          : { withCredentials: true };
        
        const response = await axios.get(`${API}/auth/me`, config);
        
        const userData = response.data;
        console.log('ProtectedRoute: Auth successful, role:', userData.role);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('ProtectedRoute: Auth failed:', error.response?.status);
        // Clear invalid backdoor token
        if (token && error.response?.status === 401) {
          console.log('ProtectedRoute: Clearing invalid backdoor token');
          sessionStorage.removeItem('backdoor_token');
          delete axios.defaults.headers.common['Authorization'];
        }
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, [location.state, location.search, requiredRole]);
  
  // Still checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Not authenticated
  if (!isAuthenticated) {
    if (requiredRole === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (requiredRole === 'staff') {
      return <Navigate to="/login" replace />;
    } else if (requiredRole === 'partner') {
      return <Navigate to="/partner" replace />;
    }
    return <Navigate to="/guru" replace />;
  }
  
  // Check role if required
  if (requiredRole && user?.role !== requiredRole) {
    if (requiredRole === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (requiredRole === 'staff') {
      return <Navigate to="/login" replace />;
    } else if (requiredRole === 'partner') {
      return <Navigate to="/partner" replace />;
    }
    return <Navigate to="/guru" replace />;
  }
  
  return children;
}

export default ProtectedRoute;